/**
 * scanners.ts — single source for "what scanners does a team have looking at
 * a given system this tick?"
 *
 * The detection pass (advance-detection.ts) and the fog-mask renderer both
 * need the same list — team-owned ships in the system plus team-owned planets
 * in the system, each packaged as a uniform ScannerView. Centralising the
 * collection here means v2 extensions ship in one place:
 *   - v2: deep-space sensor structures on planet P in system A contribute a
 *     synthetic ScannerRow to system B's list when their cross-system range
 *     covers B. Callers never change.
 *   - v2: a sibling helper `collectTeamHyperspaceScanners` will return the
 *     scanners that can see into the mid-jump pseudo-bucket.
 *
 * Keep the signature narrow — `(state, teamId, systemId, balance)` — so the
 * detection pass and the renderer can call it identically. The balance
 * reference lets us fold in VISION_RANGE_SCALE / SCAN_RANGE_SCALE and the
 * per-size planet tables without the caller doing arithmetic.
 */

import {
  DEFAULT_DETECTION_BALANCE,
  lrsaScanBoostForLevel,
  planetDetectionForSize,
  planetScanRangeForSize,
  planetStealthForSize,
  planetVisionRangeForSize,
  type DetectionBalance,
} from './balance-detection';
import { effectiveDetection, type ScannerView } from './detection';
import { sensorsEmpireBonus, sensorsScanRangeMultiplier } from './research-effects';
import { auraBonusesForShip } from './auras';
import type { GameState, Planet, SystemId, TeamId } from '../state/types';

/**
 * v3M1 — additive LRSA scan-range boost contributed by an active Long
 * Range Sensor Array on the given planet. Returns 0 when the planet has
 * no LRSA, when the LRSA is under initial construction (level 0), or
 * when its hull has dropped to ≤ 0 (defensive — destroyed structures
 * should be culled before this runs, but cheap to check).
 *
 * Exported so the detail-panel UI can preview "this LRSA contributes +X
 * scan range" without re-walking the structure list itself.
 */
export function lrsaScanBoostForPlanet(state: GameState, planet: Planet): number {
  for (const sid of planet.structureIds) {
    const s = state.structures.get(sid);
    if (!s) continue;
    if (s.kind !== 'long-range-sensor-array') continue;
    if (s.currentHull <= 0) continue;
    return lrsaScanBoostForLevel(state.structuresData, s.level);
  }
  return 0;
}

/**
 * Alias retained for callers that want to name the collected rows distinctly
 * from the pure-math ScannerView type. Structurally identical — just a doc
 * handle for "this is a scanner row from the collector, not a hand-built
 * struct for a test".
 */
export type ScannerRow = ScannerView;

/**
 * Return every scanner contributing to `teamId`'s vision/scan footprint in
 * `systemId` this tick. Order is not semantically meaningful — the detection
 * pass takes `max(pV, pS)` across the list so permutations don't change the
 * result. planetStealthForSize is exposed via the balance module because
 * planets will also figure as *targets* for enemy scanners in a future
 * pass; kept co-located so all planet-sensor knobs live in one place.
 */
export function collectTeamScannersForSystem(
  state: GameState,
  teamId: TeamId,
  systemId: SystemId,
  balance: DetectionBalance = DEFAULT_DETECTION_BALANCE,
): ScannerRow[] {
  const rows: ScannerRow[] = [];

  // 1) Team-owned ships currently in the system (skip mid-jump — they don't
  //    see anything while in hyperspace; a v2 deep-space scanner structure
  //    will cover that case via a separate helper).
  for (const ship of state.ships.values()) {
    if (ship.systemId !== systemId) continue;
    if (ship.ownerEmpireId !== teamId) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    // v3M2.2 — ring platforms carry a chassisLevel that scales vision
    // and scan ranges beyond the template baseline. Same +20%/level
    // curve as hull, capped at 2.0 at L6.
    let chassisMult = 1.0;
    if (ship.chassisLevel !== undefined && ship.chassisLevel > 1) {
      chassisMult = Math.min(2.0, 1.0 + (ship.chassisLevel - 1) * 0.2);
    }
    rows.push({
      sx: ship.sx,
      sy: ship.sy,
      visionRange: tmpl.visionRange * chassisMult,
      // MFix8.3 — Sensors Mk research multiplies effective scan range
      // empire-wide (on top of the chassis-level scaling for ODP rings).
      scanRange:
        tmpl.scanRange * chassisMult * sensorsScanRangeMultiplier(state, empire),
      // MFix7.2 — Sensors Mk research adds a flat empire-wide bonus
      // to detectionBase (Non-combat: Sensors category in tech tree).
      // MFix8.5 — friendly aura `detectionBonus` (e.g. Magician) is
      // additive on top of the empire bonus. Aura helper short-circuits
      // to zero when no eligible emitters cover this ship.
      detectionEffective: effectiveDetection(
        tmpl.detectionBase +
          sensorsEmpireBonus(state, empire) +
          auraBonusesForShip(state, ship).detectionBonus,
        balance,
      ),
    });
  }

  // 2) Team-owned planets in the system as orbital sensors (PLANETS.md).
  //    MFix1.5 — flat-constant vision/scan/detection from balance-detection
  //    (Cruiser-comparable). Mid-jump ships are skipped above; planets are
  //    always in-system so no equivalent guard is needed here.
  //    v3M1 — `lrsaScanBoostForPlanet` adds the active LRSA's per-level
  //    bonus to scanRange (0 when no LRSA / under construction / destroyed).
  for (const planet of state.planets.values()) {
    if (planet.systemId !== systemId) continue;
    if (planet.ownerEmpireId !== teamId) continue;
    const planetEmpire = state.empires.get(planet.ownerEmpireId);
    const lrsaBoost = lrsaScanBoostForPlanet(state, planet);
    // MFix7.2 — Sensors Mk research adds the same flat empire-wide
    // bonus to planet detection as it does to ship detection.
    const sensorsBonus = planetEmpire ? sensorsEmpireBonus(state, planetEmpire) : 0;
    // MFix8.3 — Sensors Mk research multiplies the planet's combined
    // (base + LRSA) scan range. Defaults to 1.0 when no Sensors tech
    // is researched, so vanilla planet sensors are unchanged until the
    // empire moves up the ladder.
    const sensorsScanMult = planetEmpire
      ? sensorsScanRangeMultiplier(state, planetEmpire)
      : 1.0;
    rows.push({
      sx: planet.systemX,
      sy: planet.systemY,
      visionRange: planetVisionRangeForSize(planet.size, state.balanceData),
      scanRange:
        (planetScanRangeForSize(planet.size, state.balanceData) + lrsaBoost) *
        sensorsScanMult,
      detectionEffective: effectiveDetection(
        planetDetectionForSize(planet.size, state.balanceData) + sensorsBonus,
        balance,
      ),
    });
  }

  return rows;
}

// Re-export the planet stealth helper so callers (e.g. a future "planets as
// targets" pass) can reach it without hopping back to balance-detection.
export { planetStealthForSize };
